import React, { useState, useEffect } from 'react';
import { 
  FaUser, FaGraduationCap, FaSchool, FaMapMarkerAlt, 
  FaChartBar, FaTrophy, FaClock, FaCheck, FaTimes,
  FaChevronDown, FaChevronUp, FaAward, FaStar,
  FaCalendarAlt, FaHistory, FaCrown
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Header from './common/Header';
import Footer from './common/Footer';

const ProfileScreen = ({ scrollToSection }) => {
  const [userData, setUserData] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedResults, setExpandedResults] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const { user } = useAuth();

  useEffect(() => {
    fetchProfileData();
  }, []);

 const fetchProfileData = async () => {
  try {
    setLoading(true);
    
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('لم يتم العثور على رمز الدخول');
    }

     const userResponse = await fetch('https://question-manager-backend.onrender.com/api/auth/me', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
     const contentType = userResponse.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const textResponse = await userResponse.text();
      console.error('الخادم أرجع HTML:', textResponse.substring(0, 200));
      throw new Error('الخادم غير متاح حالياً. يرجى المحاولة لاحقاً.');
    }
    
    if (!userResponse.ok) {
      const errorData = await userResponse.json();
      throw new Error(errorData.message || `فشل في جلب بيانات المستخدم: ${userResponse.status}`);
    }
    
    const userData = await userResponse.json();
    setUserData(userData);

     const resultsResponse = await fetch('https://question-manager-backend.onrender.com/api/result/user', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
     const resultsContentType = resultsResponse.headers.get('content-type');
    if (!resultsContentType || !resultsContentType.includes('application/json')) {
      const textResponse = await resultsResponse.text();
      console.error('الخادم أرجع HTML للنتائج:', textResponse.substring(0, 200));
      throw new Error('الخادم غير متاح حالياً. يرجى المحاولة لاحقاً.');
    }
    
    if (!resultsResponse.ok) {
      const errorData = await resultsResponse.json();
      throw new Error(errorData.message || `فشل في جلب النتائج: ${resultsResponse.status}`);
    }
    
    const resultsData = await resultsResponse.json();
    setResults(resultsData.data || []); 
    
  } catch (err) {
    console.error('Error fetching profile data:', err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  const toggleResultExpansion = (resultId) => {
    setExpandedResults(prev => ({
      ...prev,
      [resultId]: !prev[resultId]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPerformanceLevel = (score) => {
    if (score >= 95) return { level: "ممتاز", color: "text-green-600", bgColor: "bg-green-100" };
    if (score >= 85) return { level: "متميز", color: "text-blue-600", bgColor: "bg-blue-100" };
    if (score >= 75) return { level: "جيد جداً", color: "text-indigo-600", bgColor: "bg-indigo-100" };
    if (score >= 65) return { level: "جيد", color: "text-amber-600", bgColor: "bg-amber-100" };
    return { level: "مقبول", color: "text-orange-600", bgColor: "bg-orange-100" };
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600";
    if (score >= 80) return "text-blue-600";
    if (score >= 70) return "text-amber-600";
    return "text-red-600";
  };

  const filteredResults = results.filter(result => {
    if (activeTab === 'all') return true;
    if (activeTab === 'excellent') return result.score >= 90;
    if (activeTab === 'good') return result.score >= 70 && result.score < 90;
    if (activeTab === 'average') return result.score < 70;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#320A6B] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">حدث خطأ</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchProfileData}
            className="px-4 py-2 bg-[#320A6B] text-white rounded-lg hover:bg-[#2a095a] transition-colors"
          >
            حاول مرة أخرى
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Header scrollToSection={scrollToSection} />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
         <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
             <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-gradient-to-br from-[#320A6B] to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {userData?.name?.charAt(0) || 'م'}
              </div>
            </div>
            
             <div className="flex-1 text-center md:text-right">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {userData?.name || 'مستخدم'}
              </h1>
              <p className="text-gray-600 mb-4">@{userData?.username || 'username'}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-700">
                  <FaGraduationCap className="text-[#320A6B]" />
                  <span>{userData?.class || 'غير محدد'}</span>
                </div>
                
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-700">
                  <FaSchool className="text-[#320A6B]" />
                  <span>{userData?.school || 'غير محدد'}</span>
                </div>
                
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-700">
                  <FaMapMarkerAlt className="text-[#320A6B]" />
                  <span>{userData?.city || 'غير محدد'}</span>
                </div>
              </div>
            </div>
            
             <div className="bg-gray-50 rounded-xl p-4 min-w-[120px]">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#320A6B] mb-1">
                  {results.length}
                </div>
                <div className="text-sm text-gray-600">اختبار مكتمل</div>
              </div>
            </div>
          </div>
        </div>

         <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'all' 
                  ? 'bg-[#320A6B] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              جميع النتائج
            </button>
            <button
              onClick={() => setActiveTab('excellent')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'excellent' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ممتاز
            </button>
            <button
              onClick={() => setActiveTab('good')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'good' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              جيد
            </button>
            <button
              onClick={() => setActiveTab('average')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'average' 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              متوسط
            </button>
          </div>
        </div>

         <div className="space-y-4">
          {filteredResults.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">لا توجد نتائج</h3>
              <p className="text-gray-600">لم تقم بأي اختبارات بعد</p>
            </div>
          ) : (
            filteredResults.map((result, index) => {
              const performance = getPerformanceLevel(result.score);
              
              return (
                <div 
                  key={result._id || index}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
                >
                   <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleResultExpansion(result._id || index)}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            {result.testTitle}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${performance.bgColor} ${performance.color}`}>
                            {performance.level}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <FaCalendarAlt className="text-gray-400" />
                            <span>{formatDate(result.date)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FaClock className="text-gray-400" />
                            <span>{result.duration}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FaChartBar className="text-gray-400" />
                            <span>{result.totalQuestions} أسئلة</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                         <div className="text-center">
                          <div className={`text-2xl font-bold ${getScoreColor(result.score)}`}>
                            {result.score}%
                          </div>
                          <div className="text-xs text-gray-500">النسبة</div>
                        </div>
                        
                         <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            {result.correctAnswers}
                          </div>
                          <div className="text-xs text-gray-500">صحيحة</div>
                        </div>
                        
                         {expandedResults[result._id || index] ? (
                          <FaChevronUp className="text-gray-400" />
                        ) : (
                          <FaChevronDown className="text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                   {expandedResults[result._id || index] && (
                    <div className="border-t border-gray-200 p-4 animate-fade-in">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                         <div className="space-y-4">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <FaChartBar className="text-[#320A6B]" />
                            الإحصائيات الأساسية
                          </h4>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                              <div className="flex items-center gap-2">
                                <FaCheck className="text-green-600" />
                                <div>
                                  <div className="text-sm text-gray-600">صحيحة</div>
                                  <div className="font-bold text-gray-900">{result.correctAnswers}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                              <div className="flex items-center gap-2">
                                <FaTimes className="text-red-600" />
                                <div>
                                  <div className="text-sm text-gray-600">خاطئة</div>
                                  <div className="font-bold text-gray-900">{result.wrongAnswers}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center gap-2">
                                <FaClock className="text-gray-600" />
                                <div>
                                  <div className="text-sm text-gray-600">لم تُجب</div>
                                  <div className="font-bold text-gray-900">{result.unansweredQuestions}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                              <div className="flex items-center gap-2">
                                <FaAward className="text-blue-600" />
                                <div>
                                  <div className="text-sm text-gray-600">الدقة</div>
                                  <div className="font-bold text-gray-900">{result.additionalStats?.accuracy}%</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                         <div className="space-y-4">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <FaClock className="text-[#320A6B]" />
                            تحليل الوقت
                          </h4>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">المتوسط لكل سؤال</span>
                              <span className="font-medium">{result.timeAnalysis?.averageTime} ثانية</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">الوقت الإجمالي</span>
                              <span className="font-medium">{formatTime(result.totalTimeSeconds)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">أسرع إجابة</span>
                              <span className="font-medium text-green-600">{result.timeAnalysis?.fastestAnswer} ثانية</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">أبطأ إجابة</span>
                              <span className="font-medium text-red-600">{result.timeAnalysis?.slowestAnswer} ثانية</span>
                            </div>
                          </div>
                        </div>
                        
                         {result.levelResults && result.levelResults.length > 0 && (
                          <div className="md:col-span-2 space-y-4">
                            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                              <FaGraduationCap className="text-[#320A6B]" />
                              النتائج حسب المستوى
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {result.levelResults.map((level, levelIndex) => (
                                <div 
                                  key={levelIndex}
                                  className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                                >
                                  <div className="flex justify-between items-center mb-2">
                                    <h5 className="font-medium text-gray-900">{level.name}</h5>
                                    <span className={`text-sm font-bold ${
                                      level.score >= 90 ? 'text-green-600' :
                                      level.score >= 70 ? 'text-blue-600' :
                                      'text-amber-600'
                                    }`}>
                                      {level.score}%
                                    </span>
                                  </div>
                                  
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">الصحيحة:</span>
                                      <span className="font-medium">{level.correctAnswers}/{level.totalQuestions}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">الدقة:</span>
                                      <span className="font-medium">{Math.round((level.correctAnswers / level.totalQuestions) * 100)}%</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                         {(result.strengthsAndWeaknesses?.strengths?.length > 0 || result.strengthsAndWeaknesses?.weaknesses?.length > 0) && (
                          <div className="md:col-span-2 space-y-4">
                            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                              <FaStar className="text-[#320A6B]" />
                              نقاط القوة والضعف
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                               {result.strengthsAndWeaknesses.strengths.length > 0 && (
                                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                  <h5 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                                    <FaCheck className="text-green-600" />
                                    نقاط القوة
                                  </h5>
                                  <div className="space-y-2">
                                    {result.strengthsAndWeaknesses.strengths.map((strength, idx) => (
                                      <div key={idx} className="flex justify-between items-center">
                                        <span className="text-green-700">{strength.name}</span>
                                        <span className="font-bold text-green-600">{strength.ratio}%</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                               {result.strengthsAndWeaknesses.weaknesses.length > 0 && (
                                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                  <h5 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                                    <FaTimes className="text-red-600" />
                                    نقاط الضعف
                                  </h5>
                                  <div className="space-y-2">
                                    {result.strengthsAndWeaknesses.weaknesses.map((weakness, idx) => (
                                      <div key={idx} className="flex justify-between items-center">
                                        <span className="text-red-700">{weakness.name}</span>
                                        <span className="font-bold text-red-600">{weakness.ratio}%</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      
      <Footer scrollToSection={scrollToSection} />

      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ProfileScreen;